/**
 * Last-synced store
 *
 * The page-side half of the service worker's freshness reporting. The
 * worker stamps every API response it caches with the moment it came off
 * the network, and posts that timestamp to open pages -- both when it
 * refreshes a response and when it serves a stale one. This module receives
 * those messages, keeps the newest timestamp, mirrors it to localStorage so
 * it survives a reload, and hands it to whoever asks.
 *
 * Task 5.3a only records and exposes the value. Nothing renders it; the
 * last-synced UI is task 5.3b, which should read it via `getLastSyncedAt()`
 * for an initial value and `subscribeLastSynced()` for updates.
 */

import { LAST_SYNCED_MESSAGE_TYPE } from '../serviceWorker/cacheRules';

/** localStorage key holding the epoch-milliseconds timestamp. */
export const LAST_SYNCED_STORAGE_KEY = 'lastSyncedAt';

export { LAST_SYNCED_MESSAGE_TYPE };

const listeners = new Set();

/**
 * The most recent successful sync, or null if the app has never reached the
 * API on this device.
 *
 * @returns {number|null} epoch milliseconds
 */
export function getLastSyncedAt() {
  let raw = null;
  try {
    raw = window.localStorage.getItem(LAST_SYNCED_STORAGE_KEY);
  } catch {
    // Private-mode or blocked storage. Treat as "never synced".
    return null;
  }

  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Record a sync timestamp.
 *
 * Only ever moves forward: the worker reports the age of whatever it
 * served, so a cache hit on an old response must not drag "last synced"
 * backwards past a fresher response served moments earlier.
 *
 * @param {number} timestamp epoch milliseconds
 * @returns {boolean} whether this call advanced the stored value
 */
export function recordLastSyncedAt(timestamp) {
  const value = Number(timestamp);
  if (!Number.isFinite(value) || value <= 0) return false;

  const current = getLastSyncedAt();
  if (current !== null && value <= current) return false;

  try {
    window.localStorage.setItem(LAST_SYNCED_STORAGE_KEY, String(value));
  } catch {
    // Fall through: still notify, so a running page stays correct even when
    // the value cannot be persisted for the next one.
  }

  listeners.forEach((listener) => {
    try {
      listener(value);
    } catch (error) {
      console.error('lastSyncedStore listener failed:', error);
    }
  });

  return true;
}

/**
 * Observe changes to the last-synced timestamp.
 *
 * @param {(timestamp: number) => void} listener
 * @returns {() => void} unsubscribe
 */
export function subscribeLastSynced(listener) {
  if (typeof listener !== 'function') return () => {};
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Start listening for the service worker's freshness messages.
 *
 * Safe to call when there is no service worker at all (development, or an
 * unsupported browser): it becomes a no-op and `getLastSyncedAt()` simply
 * keeps returning whatever a previous session stored.
 *
 * @param {ServiceWorkerContainer} [container]
 * @returns {() => void} stop listening
 */
export function startLastSyncedListener(
  container = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined
) {
  if (!container || typeof container.addEventListener !== 'function') {
    return () => {};
  }

  const handleMessage = (event) => {
    const data = event && event.data;
    if (!data || data.type !== LAST_SYNCED_MESSAGE_TYPE) return;
    recordLastSyncedAt(data.timestamp);
  };

  container.addEventListener('message', handleMessage);
  return () => {
    container.removeEventListener('message', handleMessage);
  };
}
